import type { NormalizedLandmark, NormalizedPoint } from '../types/geometry.js';
import { clamp, degrees, distance } from '../types/geometry.js';
import type { Confidence } from '../types/metrics.js';
import { LANDMARKS, pick } from './landmarks.js';

/**
 * Head pose estimation.
 *
 * Two independent sources are supported:
 *  - `matrix`: MediaPipe's 4x4 facial transformation matrix — authoritative.
 *  - `geometric`: landmark-asymmetry heuristics — used in Node tests and when
 *    the model does not return a matrix.
 *
 * The geometric path is deliberately conservative: its confidence is low, so
 * the pose check degrades to `uncertain` instead of pretending to know.
 */

export interface PoseEstimate {
  roll: number;
  yaw: number;
  pitch: number;
  source: 'matrix' | 'geometric';
  confidence: Confidence;
}

export interface MatrixPose {
  yaw: number;
  pitch: number;
  roll: number;
}

/**
 * Decomposes a column-major 4x4 transformation matrix (as returned by
 * MediaPipe, 16 numbers) into yaw/pitch/roll in degrees.
 *
 * Convention: `roll` is rotation about the camera Z axis (head tilt),
 * `yaw` about Y (turn left/right), `pitch` about X (chin up/down). Only the
 * magnitudes are consumed by the rules, and the geometric estimator supplies
 * the sign, so a handedness mismatch cannot flip a pass into a fail.
 */
export const matrixToPose = (data: readonly number[]): MatrixPose | null => {
  if (data.length < 16) return null;
  for (const value of data) if (!Number.isFinite(value)) return null;

  // column-major flat -> row-major R[row][col]
  const r = (row: number, col: number): number => data[col * 4 + row] as number;

  const r00 = r(0, 0);
  const r10 = r(1, 0);
  const r20 = r(2, 0);
  const r21 = r(2, 1);
  const r22 = r(2, 2);
  const r11 = r(1, 1);
  const r12 = r(1, 2);

  // Decomposition of R = Rz(roll) * Ry(yaw) * Rx(pitch):
  //   pitch = atan2(R21, R22), yaw = atan2(-R20, sy), roll = atan2(R10, R00)
  const sy = Math.hypot(r00, r10);
  let roll: number;
  let pitch: number;
  let yaw: number;
  if (sy > 1e-6) {
    pitch = Math.atan2(r21, r22);
    yaw = Math.atan2(-r20, sy);
    roll = Math.atan2(r10, r00);
  } else {
    pitch = Math.atan2(-r12, r11);
    yaw = 0;
    roll = 0;
  }
  return { yaw: degrees(yaw), pitch: degrees(pitch), roll: degrees(roll) };
};

/**
 * Roll from the eye line alone — this is the most reliable signal and the one
 * the overlay draws, so it is always computed geometrically.
 *
 * Landmarks are normalized: x by the image **width**, y by the image **height**.
 * On a non-square frame the two axes carry different pixel scales, so an angle
 * taken from the raw normalized deltas is inflated by `width / height`:
 * `17.png` (1312×1199) is tilted −21.9° and used to be reported as −23.7°, while
 * square frames were unaffected. `aspect` (= `width / height`) puts both axes
 * back into pixels, which is exactly the definition the brief uses:
 * `atan2(rightEye.y − leftEye.y, rightEye.x − leftEye.x)` in original pixels.
 */
export const rollFromEyes = (
  eyeLeft: NormalizedPoint,
  eyeRight: NormalizedPoint,
  aspect = 1,
): number => {
  const dx = (eyeRight.x - eyeLeft.x) * aspect;
  const dy = eyeRight.y - eyeLeft.y;
  if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) return 0;
  // In image coordinates y grows downwards; a positive result means the right
  // eye is lower, i.e. the head is tilted clockwise.
  return degrees(Math.atan2(dy, dx));
};

/**
 * Yaw from the horizontal asymmetry of the nose relative to the face edges.
 * `+90 * ratio` is the classic small-angle approximation used by ID-photo
 * tools and is accurate to a few degrees within the ±25° range that matters.
 */
export const yawFromLandmarks = (
  landmarks: readonly NormalizedLandmark[],
  noseTip: NormalizedPoint,
): number | null => {
  const leftEdge = pick(landmarks, LANDMARKS.leftCheekEdge);
  const rightEdge = pick(landmarks, LANDMARKS.rightCheekEdge);
  if (!leftEdge || !rightEdge) return null;
  // Make the naming independent of a mirrored model.
  const imageLeft = leftEdge.x <= rightEdge.x ? leftEdge : rightEdge;
  const imageRight = leftEdge.x <= rightEdge.x ? rightEdge : leftEdge;
  const dLeft = Math.abs(noseTip.x - imageLeft.x);
  const dRight = Math.abs(imageRight.x - noseTip.x);
  if (dLeft + dRight < 1e-6) return null;
  // Positive = nose closer to the image-left edge = head turned to its own right.
  return clamp((90 * (dLeft - dRight)) / (dLeft + dRight), -45, 45);
};

/**
 * Pitch from the vertical position of the nose tip between the eye line and
 * the chin. A frontal adult face puts the nose tip at roughly 45% of that
 * span; the deviation is converted to degrees with a calibrated gain.
 */
export const pitchFromLandmarks = (
  landmarks: readonly NormalizedLandmark[],
  noseTip: NormalizedPoint,
  eyeLineY: number,
): number | null => {
  const chin = pick(landmarks, LANDMARKS.chinBottom);
  if (!chin) return null;
  const span = chin.y - eyeLineY;
  if (Math.abs(span) < 1e-6) return null;
  const ratio = (noseTip.y - eyeLineY) / span;
  const FRONTAL_RATIO = 0.45;
  const GAIN_DEG = 120;
  return clamp((ratio - FRONTAL_RATIO) * GAIN_DEG, -40, 40);
};

export interface GeometricPoseInput {
  landmarks: readonly NormalizedLandmark[];
  eyeLeft: NormalizedPoint;
  eyeRight: NormalizedPoint;
  eyeLineY: number;
  noseTip: NormalizedPoint;
  /** Quality of the landmark fit in [0, 1]; scales the reported confidence. */
  landmarkQuality?: number;
  /** Image `width / height`, so the eye-line angle is measured in pixels. */
  aspect?: number;
}

export const geometricPose = (input: GeometricPoseInput): PoseEstimate => {
  const roll = rollFromEyes(input.eyeLeft, input.eyeRight, input.aspect ?? 1);
  const yaw = yawFromLandmarks(input.landmarks, input.noseTip) ?? 0;
  const pitch = pitchFromLandmarks(input.landmarks, input.noseTip, input.eyeLineY) ?? 0;
  const quality = input.landmarkQuality ?? 0.6;
  return {
    roll,
    yaw,
    pitch,
    source: 'geometric',
    confidence: {
      value: clamp(quality * 0.45, 0, 0.5),
      reason:
        'Углы наклона оценены геометрически по landmarks; матрица позы модели недоступна.',
    },
  };
};

/**
 * Combines the matrix estimate with the geometric one.
 *
 * The matrix supplies yaw and pitch — the two angles that cannot be measured
 * from a frontal landmark set. **Roll is always taken from the eye line**
 * (`left eye → right eye`): the matrix folds yaw and pitch into its roll
 * component, so a perfectly straight head turned slightly towards the camera
 * used to be reported as a several-degree tilt, which then failed the
 * "head not tilted" requirement.
 */
export const combinePose = (
  geometric: PoseEstimate,
  matrix: MatrixPose | null,
  matrixConfidence = 0.9,
): PoseEstimate => {
  if (!matrix) return geometric;

  const signOf = (value: number, fallback: number): number => {
    if (Math.abs(value) < 0.5) return Math.abs(fallback) < 0.5 ? 1 : Math.sign(fallback);
    return Math.sign(value);
  };

  const merge = (matrixValue: number, geometricValue: number): number => {
    const magnitude = Math.abs(matrixValue);
    // Never let the matrix talk us out of a large geometric deviation.
    const magnitudeSafe = Math.max(magnitude, Math.abs(geometricValue) * 0.5);
    return signOf(matrixValue, geometricValue) * magnitudeSafe;
  };

  const disagreement = Math.max(
    Math.abs(Math.abs(matrix.yaw) - Math.abs(geometric.yaw)),
    Math.abs(Math.abs(matrix.pitch) - Math.abs(geometric.pitch)),
  );
  const confidence = clamp(matrixConfidence - disagreement / 60, 0.3, matrixConfidence);

  return {
    // In-plane tilt: measured on the eye line, never read from the matrix.
    roll: geometric.roll,
    yaw: merge(matrix.yaw, geometric.yaw),
    pitch: merge(matrix.pitch, geometric.pitch),
    source: 'matrix',
    confidence: {
      value: confidence,
      reason:
        disagreement > 12
          ? 'Матрица позы и геометрическая оценка расходятся — результат требует проверки.'
          : undefined,
    },
  };
};

/** Distance between the eye centres, normalized to image width. */
export const eyeDistance = (eyeLeft: NormalizedPoint, eyeRight: NormalizedPoint): number =>
  distance(eyeLeft, eyeRight);
